/* eslint-disable react/prop-types */
import {
  AFPTable,
  AFPTableRowAction,
  Checkbox,
  CounterTag,
  Pagination,
  StatusBadge,
  FilterTableFrame,
  EmptyState,
  Spinner,
} from '@gsa/afp-component-library';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import HtmlDiff from 'htmldiff-js';
import { isEmpty } from 'lodash';
import ReactHtmlParser from 'react-html-parser';
import queryString from 'query-string';
import useDidMountEffect from '../../../../hooks/use-did-mount';
import { useAppAbility, useCurrentUser } from '@gsa/afp-shared-ui-utils';
import CommentForm from '../../components/comment-form';
import CommentsTable from './comments-table';
import RequirementsFilter from './requirements-filter';
import '../../../standard-items/styles/standard-items-table.scss';
import { OPERATIONS, SUBJECTS } from '../../../../utilities/constants';
import Truncate from '../../../../components/Truncate';
import DiffHighlighter from '../../components/diff-highlighter';
import TypeHighlighter from '../../components/type-highlighter';
import MinimumsHighlighter from '../../components/minimums-highlighter';
import {
  canViewComment,
  getAuthor,
  getRole,
} from '../../../../utilities/types/role-types';
import useQuery from '../../../../hooks/useQuery';
import { useFederalStandards } from '../../fvs-provider';
import { useFvsCommentsContext } from '../../fvs-comments-provider';
import { useFvsRequirements } from '../requirements-provider';
import '../styles/requirements.scss';
import { FVS_RT_ACTIONS, FVS_RT_MODAL_MODES } from '../constants';

const RequirementsTable = () => {
  const location = useLocation();
  const ability = useAppAbility();
  const query = useQuery();

  const {
    standardItem,
    selectedOptions,
    selectedYear,
    setFvsContextData,
    isCommentingAllowed4Fvs4User,
  } = useFederalStandards();

  const {
    getEquipmentAssociations,
    getEquipmentAssociationsWithChanges,
    equipmentAssociations,
    groupMultipleOptions,
    doHighlightChanges,
    setFvsRequirementsContextData,
    isRequirementLoading: providerLoading,
  } = useFvsRequirements();

  const { addComment, getStandardsComments, isDeletingComment } =
    useFvsCommentsContext();

  const { roles, id } = useCurrentUser()?.currentUser || {};

  const allowedActions = (props) => {
    const { original } = props.row;
    const isEquipmentCodeReadOnly =
      original?.standardItem?.isStandardItemInOpenSolicitation;
    const actions = [];
    const isEditable =
      ability.can(OPERATIONS.Manage, SUBJECTS.Requirements) &&
      !isEquipmentCodeReadOnly &&
      original?.canEdit;
    if (isEditable) {
      actions.push({
        icon: 'edit',
        label: 'Edit Equipment Code Association',
      });
    }
    actions.push({ icon: 'visibility', label: 'Comments' });
    if (
      ability.can(OPERATIONS.Manage, SUBJECTS.Requirements) &&
      !isEquipmentCodeReadOnly &&
      original?.canDelete
    ) {
      actions.push({ icon: 'delete', label: 'Remove' });
    }
    return actions;
  };

  // Local loading state to show spinner immediately when API is called
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const isRequirementLoading = isLocalLoading || providerLoading;
  const { code, commentId } = queryString.parse(location.search);

  const defaultFilters = useMemo(
    () => ({
      operator: 'AND',
      value: [
        {
          key: '$standardItem.year$',
          operator: 'EQ',
          value: selectedYear,
        },
        {
          key: '$standardItem.standard_item_number$',
          operator: 'EQ',
          value: selectedOptions?.standardItemNumber,
        },
        {
          key: '$equipmentCode.is_active$',
          operator: 'EQ',
          value: 1,
        },
        {
          key: '$equipmentCode.deleted_at$',
          operator: 'EQ',
          value: null,
        },
      ],
    }),
    [selectedYear, selectedOptions?.standardItemNumber],
  );

  const [order, setOrder] = useState([['equipmentCode', 'title', 'asc']]);
  const [paginationState, setPaginationState] = useState({
    limit: 50,
    offset: 0,
    currentPage: 1,
  });
  const [filters, setFilters] = useState(defaultFilters);
  const [tableData, setTableData] = useState([]);
  const tableRef = React.createRef();
  const filterSetByPanelRef = React.useRef(false);
  const [showFilters] = useState(true);

  useEffect(() => {
    if (commentId) {
      setFvsRequirementsContextData(
        FVS_RT_ACTIONS.SET_RT_MODAL_MODE,
        FVS_RT_MODAL_MODES.COMMENT,
      );
    }
  }, [commentId]);

  useEffect(() => {
    if (code) {
      setFvsContextData('SET_EQUIPMENT_CODE_QUERY_STRING', code);
    }
  }, [code]);

  const getAssociationData = () => {
    // Validate that we have required filters before making the API call
    const hasYearFilter = filters?.value?.some(
      (f) =>
        f.key === '$standardItem.year$' &&
        f.value !== undefined &&
        f.value !== null &&
        f.value !== '',
    );
    const hasStandardItemFilter = filters?.value?.some(
      (f) =>
        f.key === '$standardItem.standard_item_number$' &&
        f.value !== undefined &&
        f.value !== null &&
        f.value !== '',
    );

    // Don't make the API call if required filters are missing
    if (!hasYearFilter || !hasStandardItemFilter) {
      return;
    }

    const { limit, offset } = paginationState;

    setIsLocalLoading(true);

    if (doHighlightChanges) {
      getEquipmentAssociationsWithChanges({
        variables: { filters, limit, offset, order },
      });
    } else {
      getEquipmentAssociations({
        variables: { filters, limit, offset, order },
      });
    }
  };

  const role = getRole(roles);

  const loadComments = () => {
    if (!standardItem?.id) return;

    const filter = {
      operator: 'AND',
      value: [
        {
          key: 'linkedEntityId',
          operator: 'EQ',
          value: standardItem?.id,
        },
      ],
    };

    if (!ability?.can(OPERATIONS.Manage, SUBJECTS.FVSComments)) {
      filter.value.push({
        key: 'status',
        operator: 'NOTIN',
        value: [4],
      });
    }
    getStandardsComments?.({
      variables: {
        filters: filter,
      },
    });
  };

  useEffect(() => {
    loadComments();
  }, [filters, standardItem?.id]);

  // Sync filters and reset pagination when year or standardItem changes.
  // Skip if filters were just set by the filter panel to avoid overwriting
  // user-selected filters (category, type, code, title, etc.).
  useEffect(() => {
    if (filterSetByPanelRef.current) {
      filterSetByPanelRef.current = false;
      return;
    }
    setFilters(defaultFilters);
    setPaginationState((prev) => {
      if (prev.offset === 0 && prev.currentPage === 1) return prev;
      return { ...prev, offset: 0, currentPage: 1 };
    });
  }, [defaultFilters]);

  useEffect(() => {
    if (isEmpty(standardItem?.standardItemNumber)) {
      setTableData([]);
    }
  }, [standardItem?.standardItemNumber]);

  // Single data-fetching effect — includes filters so year changes trigger a refetch
  useEffect(() => {
    getAssociationData();
  }, [
    filters,
    order,
    paginationState,
    doHighlightChanges,
    groupMultipleOptions,
    isDeletingComment,
  ]);

  const handleSelectedAction = useCallback((evt, row) => {
    if (evt === 'Comments' && row.isExpanded === undefined) {
      row.toggleRowExpanded();
    } else if (evt === 'Edit Equipment Code Association' || evt === 'Remove') {
      const { original: equipmentAssociation } = row;
      setFvsRequirementsContextData(
        'SET_EQUIPMENT_ASSOCIATION',
        equipmentAssociation,
      );
      setFvsRequirementsContextData(
        'SET_RT_MODAL_MODE',
        evt === 'Remove'
          ? FVS_RT_MODAL_MODES.CONFIRM_DELETE_ASSOCIATION
          : FVS_RT_MODAL_MODES.EDIT_ASSOCIATION,
      );
    }
    return null;
  }, []);

  const handleCommentSubmit = async (c, original) => {
    await addComment({
      variables: {
        comment: {
          comment: c,
          year: Number(selectedYear),
          authorType: getAuthor(roles),
          status: 0,
          publishStatus: 0,
          linkedEntityType: 2,
          linkedEntityId: Number(original?.id),
          fvsCode: selectedOptions.fedStandardCode,
          standardItemId: Number(standardItem?.id),
        },
      },
    });
    getAssociationData();
    loadComments();
  };

  const getCategoryTitle = (cat) => {
    const equipmentCodeCategory = groupMultipleOptions?.category?.find(
      (c) => c.value === cat,
    );
    return equipmentCodeCategory?.label;
  };

  const updateEquipmentAssociationsWithCategoryTitle = () => {
    return equipmentAssociations?.rows?.map((ea) => {
      const comments = ea.comments?.filter((comment) =>
        canViewComment(comment, id, role),
      );
      const commentsCount = comments?.length;
      return {
        ...ea,
        'equipmentCode.category': getCategoryTitle(ea?.equipmentCode?.category),
        comments,
        commentsCount,
      };
    });
  };

  // Populate standardItem from equipment associations when it's not set by the provider
  useEffect(() => {
    if (!standardItem && equipmentAssociations?.rows?.[0]?.standardItem) {
      setFvsContextData(
        'SET_SELECTED_STANDARD_ITEM_OBJ',
        equipmentAssociations.rows[0].standardItem,
      );
    }
  }, [standardItem, equipmentAssociations]);

  useEffect(() => {
    // Clear local loading state when data arrives or changes
    setIsLocalLoading(false);

    if (equipmentAssociations?.rows && equipmentAssociations.rows.length > 0) {
      const newData = updateEquipmentAssociationsWithCategoryTitle();
      setTableData(newData || []);
    } else {
      setTableData([]);
    }
  }, [equipmentAssociations, groupMultipleOptions]);

  useEffect(() => {
    const ec = query?.get('equipmentCode');
    const type = query?.get('type');
    if (type === 'ec') {
      const filter = {
        operator: 'AND',
        value: [
          {
            key: '$standardItem.year$',
            operator: 'EQ',
            value: selectedYear,
          },
          {
            key: '$standardItem.standard_item_number$',
            operator: 'EQ',
            value: selectedOptions?.standardItemNumber,
          },
          {
            key: '$equipmentCode.code$',
            operator: 'EQ',
            value: `${ec}`,
          },
        ],
      };

      setFilters(filter);
    } else if (type === 'si') {
      // TODO
    }
  }, []);

  useDidMountEffect(() => {
    // Reset to first page when filters change
    setPaginationState((prev) => ({
      ...prev,
      offset: 0,
      currentPage: 1,
    }));
  }, [filters]);

  const columns = useMemo(
    () => [
      {
        Header: 'Title',
        accessor: 'equipmentCode.title',
        Cell: ({ value, row }) => {
          return (
            <>
              {row?.original?.change?.isInserted === 'Yes' && (
                <span className="padding-right-1">
                  <StatusBadge variant="Ready-Gray">New</StatusBadge>
                </span>
              )}
              <DiffHighlighter
                entity={row?.original?.equipmentCode}
                value={value}
                fieldName="title"
              />
            </>
          );
        },
      },
      {
        Header: 'Code',
        accessor: 'equipmentCode.code',
        Cell: ({ value }) => {
          return <div>{value}</div>;
        },
      },
      {
        Header: 'Standard details',
        accessor: 'associationText',
        sortable: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ value, row }) => {
          return (
            <DiffHighlighter
              entity={row?.original}
              value={value}
              fieldName="associationText"
            />
          );
        },
      },
      {
        Header: 'Minimums',
        accessor: 'inputType.title',
        Cell: MinimumsHighlighter,
      },
      {
        Header: 'Type',
        accessor: 'associationType.title',
        Cell: ({ value, row }) => {
          return (
            <TypeHighlighter
              entity={row?.original}
              value={value}
              fieldName="associationTypeCode"
            />
          );
        },
      },
      {
        Header: 'Category',
        accessor: 'equipmentCode.category',
        Cell: ({ row }) => {
          return (
            <DiffHighlighter
              entity={row?.original?.equipmentCode}
              value={row?.original?.equipmentCode?.category}
              fieldName="category"
            />
          );
        },
      },
      {
        Header: 'Comments',
        accessor: 'comments',
        sortable: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => {
          let count = 0;
          if (!ability.can(OPERATIONS.Manage, SUBJECTS.FVSComments)) {
            count = value?.filter((item) => {
              return item.status !== 'Invalid';
            }).length;
          } else {
            count = value?.length;
          }

          if (count > 0) {
            return <CounterTag count={count} />;
          }
          return null;
        },
      },
      {
        Header: 'Actions',
        sortable: false,
        Cell: (props) => (
          <AFPTableRowAction
            {...props}
            actions={allowedActions(props)}
            // eslint-disable-next-line react/prop-types
            onSelectAction={(evt) => handleSelectedAction(evt, props.row)}
          />
        ),
      },
    ],
    [],
  );

  const renderRowSubComponent = useCallback(
    ({ row: { original } }) => {
      // need to hide invalid comments
      const canManage = ability.can(OPERATIONS.Manage, SUBJECTS.FVSComments);
      const filteredComments = original?.comments?.filter((comment) => {
        if (canManage) {
          return comment;
        }
        return comment.status !== 'Invalid';
      });

      return (
        <div className="">
          <div className="grid-row grid-gap">
            <div className="tablet:grid-col">
              <div className="grid-row">
                <div className="tablet:grid-col">
                  <div className="text-bold">
                    <DiffHighlighter
                      entity={original?.equipmentCode}
                      value={original?.equipmentCode?.title}
                      fieldName="title"
                    />
                  </div>
                  <div className="tablet:grid-col">
                    {original?.equipmentCode?.content?.content &&
                      doHighlightChanges &&
                      ReactHtmlParser(`<div>
                    ${HtmlDiff.execute(
                      original?.equipmentCode?.change?.prevVersionDesc,
                      original?.equipmentCode?.content?.content,
                    )}
                    </div>`)}

                    {original?.equipmentCode?.content?.content &&
                      !doHighlightChanges && (
                        <Truncate
                          content={original?.equipmentCode?.content?.content}
                          size={250}
                        />
                      )}

                    {!original?.equipmentCode?.content?.content && (
                      <div className="text-bold">&ndash;</div>
                    )}
                  </div>
                  <div className="tablet:grid-col margin-top-3">
                    <div className="text-bold">Equipment Code</div>
                    {original?.equipmentCode?.code ? (
                      <div>{original?.equipmentCode?.code}</div>
                    ) : (
                      <span>&ndash;</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {isCommentingAllowed4Fvs4User && (
              <div className="tablet:grid-col">
                <CommentForm
                  addComment={
                    (comment) => handleCommentSubmit(comment, original)
                    // eslint-disable-next-line
                  }
                />
              </div>
            )}
          </div>
          {filteredComments?.length ? (
            <CommentsTable
              comments={filteredComments}
              rowDetail={original}
              role={role}
            />
          ) : null}
        </div>
      );
    },
    [isCommentingAllowed4Fvs4User],
  );

  const handlePaginationChange = (currentPage, itemsPerPage) => {
    // Calculate new offset.
    const offset = (currentPage - 1) * itemsPerPage;

    setPaginationState({
      limit: itemsPerPage,
      offset,
      currentPage,
    });
  };

  // Pagination reset is now handled in the defaultFilters sync effect above

  const TableWrapper = ({ tableProps, paginationProps }) => {
    return (
      <>
        <AFPTable
          expandable
          columns={tableProps.columns}
          data={tableProps.data || []}
          renderRowSubComponent={tableProps.renderRowSubComponent}
          onSort={tableProps.onSort}
          defaultSort={tableProps.defaultSort}
          fullWidth
          ref={tableProps.ref}
        />
        {isRequirementLoading && (
          <div className="text-center margin-top-5">
            <Spinner aria-busy="true" size="small" />
            <div>Loading requirements...</div>
          </div>
        )}
        {!isRequirementLoading && tableProps.data?.length > 0 && (
          <Pagination {...paginationProps} />
        )}
        {!isRequirementLoading && !tableProps.data?.length && (
          <div
            className="text-center margin-top-neg-2 height-full"
            style={{ height: '165vh' }}
          >
            <EmptyState
              hasBackground
              containerStyles="padding-top-9 height-full"
              topText={
                <strong>
                  No Equipment Codes are associated with this Standard Item.
                </strong>
              }
            />
          </div>
        )}
      </>
    );
  };

  const HighLightDifference = ({ onChange, value, defaultChecked }) => (
    <Checkbox
      className="float-right"
      label="Highlight differences compared to last year"
      name="chbox_highlight"
      checked={value}
      data-testid="requirements-table-highlight-changes"
      onChange={onChange}
      defaultChecked={defaultChecked}
    />
  );

  const FTF = useMemo(
    () =>
      FilterTableFrame(
        null,
        HighLightDifference,
        RequirementsFilter,
        TableWrapper,
      ),
    [],
  );

  return (
    <>
      <div className="grid-row grid-gap margin-top-3">
        <div className="desktop:grid-col-6 tablet-lg:grid-col-6 ">
          {/* eslint-disable-next-line */}
          <h2 tabIndex="0" aria-label="Requirements Table">
            Requirements Table
          </h2>
        </div>
      </div>
      <FTF
        upRightProps={{
          onChange: () => {
            setFvsRequirementsContextData(
              'SET_HIGHLIGHT_CHANGES',
              !doHighlightChanges,
            );
          },
          value: doHighlightChanges,
          defaultChecked: false,
        }}
        lowLeftProps={{
          setFilters: (newFilters) => {
            filterSetByPanelRef.current = true;
            setFilters(newFilters);
          },
        }}
        lowRightProps={{
          tableProps: {
            expandable: true,
            columns,
            data: tableData || [],
            renderRowSubComponent,
            onSort: setOrder,
            defaultSort: order,
            fullWidth: true,
            ref: tableRef,
          },
          paginationProps: {
            itemsPerPageOptions: [50, 100, 150, 200],
            onPageChange: handlePaginationChange,
            variant: 'advanced',
            itemsPerPage: paginationState.limit,
            currentPage: paginationState.currentPage,
            itemsCount: equipmentAssociations?.count,
          },
        }}
        filterToggle={showFilters}
      />
    </>
  );
};

export default RequirementsTable;
